/**
 * V1 search synonym / rescue map.
 * Source of truth for content: playmath-metadata/catalog/live/v1-search-alias-map.json
 * Prefer tags[] after CMS tags exist; slugs[] for rescue. division→Fractions is related boost.
 */

import {
  lastWordInflectionCandidates,
  normalizeSearchQuery,
  tokenizeSearchQuery,
} from "@/lib/search-query";

export type SearchAliasEntry = {
  tags: string[];
  slugs: string[];
  no_results?: boolean;
  related_concept_boost?: boolean;
};

export const SEARCH_ALIAS_MAP: Record<string, SearchAliasEntry> = {
  multiplication: {
    tags: ["Multiplication"],
    slugs: ["mm-multiplication", "mm-multiplication-2", "times-trials"],
  },
  multiply: {
    tags: ["Multiplication"],
    slugs: [],
  },
  "times tables": {
    tags: ["Multiplication"],
    slugs: ["times-trials"],
  },
  times: {
    tags: ["Multiplication"],
    slugs: ["times-trials"],
  },
  fractions: {
    tags: ["Fractions"],
    slugs: ["mm-slice-fractions", "mm-slice-fractions-2"],
  },
  fraction: {
    tags: ["Fractions"],
    slugs: ["mm-slice-fractions", "mm-slice-fractions-2"],
  },
  addition: {
    tags: ["Addition"],
    slugs: [
      "chain-sums",
      "eat-10",
      "magnet-sums",
      "snakebird-math",
      "stretch-sums",
      "sum-blocks",
      "sum-lines",
      "sum-slider",
    ],
  },
  add: {
    tags: ["Addition"],
    slugs: ["eat-10"],
  },
  sums: {
    tags: ["Addition"],
    slugs: ["eat-10", "sum-blocks", "sums-out", "snakebird-math"],
  },
  "place value": {
    tags: [],
    slugs: [
      "2048-drop",
      "dice-merge",
      "mm-big-numbers",
      "mm-multiplication",
      "mm-slice-fractions-2",
      "mm-small-numbers",
      "piggy-bank",
      "swap-sums",
    ],
  },
  division: {
    tags: ["Fractions"],
    slugs: ["mm-slice-fractions", "mm-slice-fractions-2"],
    related_concept_boost: true,
  },
  // Honest empty until a curated Subtraction tag/slug list exists (do not fuzzy-leak to Fractions).
  subtraction: {
    tags: [],
    slugs: [],
    no_results: true,
  },
  subtract: {
    tags: [],
    slugs: [],
    no_results: true,
  },
  pets: {
    tags: ["Pets"],
    slugs: ["pet-doctor", "piggy-bank", "sudocats", "sudoggos"],
  },
  pet: {
    tags: ["Pets"],
    slugs: ["pet-doctor"],
  },
  cats: {
    tags: ["Cats"],
    slugs: ["sudocats"],
  },
  cat: {
    tags: ["Cats"],
    slugs: ["sudocats"],
  },
  dogs: {
    tags: ["Dogs"],
    slugs: ["sudoggos"],
  },
  dog: {
    tags: ["Dogs"],
    slugs: ["sudoggos"],
  },
  cars: {
    tags: ["Vehicles"],
    slugs: ["car-match", "parking-jam"],
  },
  car: {
    tags: ["Vehicles"],
    slugs: ["car-match", "parking-jam"],
  },
  bus: {
    tags: ["Vehicles"],
    slugs: ["bus-jam"],
  },
  colors: {
    tags: ["Colors"],
    slugs: ["black", "blue", "color-smash", "green", "orange", "pink", "red"],
  },
  colour: {
    tags: ["Colors"],
    slugs: [],
  },
  chess: {
    tags: ["Chesslike", "Chess"],
    slugs: [
      "checkers",
      "checkers-kids",
      "chess",
      "four-in-a-row",
      "super-chesslike-adventure",
      "times-trials",
      "yin-yang",
    ],
  },
  "2048": {
    tags: ["Merge"],
    slugs: [
      "2048-dice",
      "2048-drop",
      "2048-wood-blocks",
      "dice-merge",
      "jelly-merge",
      "mergix",
      "sticky-2048",
    ],
  },
  merge: {
    tags: ["Merge"],
    slugs: [],
  },
  block: {
    tags: ["Block Puzzle"],
    slugs: [
      "block-buster-billy",
      "block-jam",
      "block-puzzle",
      "blocks-classic",
      "sand-blocks",
      "sum-blocks",
    ],
  },
  blocks: {
    tags: ["Block Puzzle"],
    slugs: [],
  },
  draw: {
    tags: ["Drawing"],
    slugs: ["draw-one-piece", "draw-perimeter", "drawsome", "line-puzzle"],
  },
  drawing: {
    tags: ["Drawing"],
    slugs: [],
  },
  "board game": {
    tags: ["Board Game Classics"],
    slugs: [],
  },
  "board games": {
    tags: ["Board Game Classics"],
    slugs: [],
  },
  minecraft: {
    tags: ["Building & Crafting"],
    slugs: ["mine-craftor", "survival-island"],
  },
  roblox: {
    tags: ["Building & Crafting"],
    slugs: ["mine-craftor"],
  },
  soccer: {
    tags: [],
    slugs: [],
    no_results: true,
  },
  football: {
    tags: [],
    slugs: [],
    no_results: true,
  },
  "geometry dash": {
    tags: [],
    slugs: [],
    no_results: true,
  },
  divide: {
    tags: ["Fractions"],
    slugs: ["mm-slice-fractions", "mm-slice-fractions-2"],
    related_concept_boost: true,
  },
  dividing: {
    tags: ["Fractions"],
    slugs: [],
    related_concept_boost: true,
  },
};

/** Phrase variants that only inflect the final word (e.g. times table ↔ times tables). */
export function aliasQueryInflectionCandidates(term: string): string[] {
  return lastWordInflectionCandidates(normalizeSearchQuery(term));
}

/**
 * Alias lookup: exact key first, then last-word singular/plural variants that exist in the map.
 * Keeps the map small while covering kid phrasing like "times table" vs "times tables".
 */
export function getSearchAlias(term: string): SearchAliasEntry | undefined {
  const key = normalizeSearchQuery(term);
  if (!key) return undefined;

  const exact = SEARCH_ALIAS_MAP[key];
  if (exact) return exact;

  for (const candidate of lastWordInflectionCandidates(key)) {
    const hit = SEARCH_ALIAS_MAP[candidate];
    if (hit) return hit;
  }

  return undefined;
}

/** True when the query is an explicit no-results alias (e.g. soccer). */
export function isExplicitNoResultsQuery(term: string): boolean {
  return getSearchAlias(term)?.no_results === true;
}

export type PreparedSearchQuery = {
  raw: string;
  normalized: string;
  tokens: string[];
  /** Full phrases/terms to run through Fuse (query + alias tag names). */
  expandedTerms: string[];
  alias: SearchAliasEntry | undefined;
  noResults: boolean;
  relatedConceptBoost: boolean;
};

/**
 * Normalize → alias lookup → expand synonym terms for the ranking pipeline.
 *
 * Alias tag names are listed on `expandedTerms` for observability only — ranking
 * resolves alias tags/slugs by exact equality and must not Fuse them as free-text.
 * Related-concept boosts omit the raw query from expansion so "division" cannot
 * fuzzy-match "Addition".
 */
export function prepareSearchQuery(raw: string): PreparedSearchQuery {
  const normalized = normalizeSearchQuery(raw);
  const alias = normalized ? getSearchAlias(normalized) : undefined;
  const tokens = tokenizeSearchQuery(normalized);
  const relatedConceptBoost = alias?.related_concept_boost === true;
  const expandedTerms = new Set<string>();

  if (normalized && !relatedConceptBoost) {
    expandedTerms.add(normalized);
  }
  if (alias) {
    for (const tag of alias.tags) {
      const tagNorm = normalizeSearchQuery(tag);
      if (tagNorm) expandedTerms.add(tagNorm);
    }
  }

  return {
    raw,
    normalized,
    tokens,
    expandedTerms: [...expandedTerms],
    alias,
    noResults: alias?.no_results === true,
    relatedConceptBoost,
  };
}
